import * as React from 'react'
import { useAssertStableReference } from '../../../../hooks/use-assert-stable-reference'
import { CastingBase } from '../../../../services/json-schemas/base/casting'
import { MarketplaceBase } from '../../../../services/json-schemas/base/marketplace'
import { OfferedServiceBase } from '../../../../services/json-schemas/base/offered_service'
import { SupplierBase } from '../../../../services/json-schemas/base/supplier'
import { I18nService } from '../../../../services/utils/i18n.service'
import { QuotationBase } from '../../../../services/json-schemas/base/quotation'
import QuotationFormUnitOfWorkHourDayMonthEventComponent from './quotation-form-unit-of-work-hour-day-month-event.component'
import QuotationFormUnitOfWorkCastingComponent from './quotation-form-unit-of-work-casting.component'
import { ItemQuotationBase } from '../../../../services/json-schemas/base/item_quotation'
import { OfferedServiceSubscriptionBase } from '../../../../services/json-schemas/base/offered_service_subscription'
import { SpecificIncreaseBase } from '../../../../services/json-schemas/base/specific_increase'
import QuotationFormEnableEmployeeOfCustomerComponent from './quotation-form-enable-employee-of-customer.component'
import { CesuQuotationBase } from '../../../../services/json-schemas/base/cesu_quotation'
import { CesuMileageReimbursementBase } from '../../../../services/json-schemas/base/cesu_mileage_reimbursement'

interface Props {
  casting: Pick<CastingBase, 'rentable_unit_of_work' | 'casting_type'> & { price_of_options_adding_price: { price_cents: number, tax_cents: number } }
  castingMarket: Pick<MarketplaceBase, 'currency'>
  currentUserMarket: Pick<SupplierBase, 'online_sign?' | 'taxes_for_select_react'>
  offeredService: Pick<OfferedServiceBase, 'round_on_vat_price' | 'employee_one_shot_fee_percentage' | 'employee_recurring_fee_percentage' | 'employee_product_fee_percentage' | 'specific_pricing_mode' | 'description_plain_text' | 'enable_employee_of_customer'> & {
    specific_increases: Pick<SpecificIncreaseBase, 'id' | 'name' | 'percentage'>[]
  }
  offeredServiceSubscription: Pick<OfferedServiceSubscriptionBase, 'employee_one_shot_fee_percentage' | 'employee_recurring_fee_percentage' | 'employee_product_fee_percentage'> | null
  taxRate: number
  quotation: Pick<QuotationBase, 'price_cents' | 'vat_price_cents' | 'round_on_vat_price' | 'signable' | 'description' | 'use_specific_increases'> & {
    item_quotations_attributes: (Pick<ItemQuotationBase, 'description' | 'kind' | 'price_cents' | 'quantity' | 'tax_rules_group_id' | 'verbose_description'> & { id?: ItemQuotationBase['id'], _destroy?: boolean })[]
    cesu_quotation_attributes: Pick<CesuQuotationBase, 'id' | 'management_fee_with_taxes_cents' | 'raw_employee_hourly_salary_cents' | 'employer_charge_percentage' | 'charge_exemption' | 'estimated_monthly_hours' | 'rest_week_day' | 'rest_week_day_worked_paid' | 'high_value_day_offs' | 'day_off_increase_percentage' | 'weekend_increase_percentage' | 'weekend_incluses_saturday' | 'apply_increases_to_management_fees'> & {
      cesu_mileage_reimbursement_attributes: Pick<CesuMileageReimbursementBase, 'id' | 'amount_cents' | 'applyness' | 'show_on_document'> & { _destroy?: boolean } | null
    }
  }
  isNoVat: boolean
  onChange: React.Dispatch<React.SetStateAction<Partial<Props['quotation']>>>
  goBack?: () => void
  noBox?: boolean
  hideSignable?: boolean
  title?: React.ReactNode
}

const i18nScoped = new I18nService(require('./quotation-form.component.yml'))

export default function QuotationFormComponent (props: Props) {
  const { casting, offeredService, isNoVat, currentUserMarket, quotation, noBox, title, hideSignable, goBack, onChange } = props

  // CesuPriceableComponent, included by multiples children components, needs a stable state callback
  useAssertStableReference(onChange)

  return <>
    { (goBack || !isNoVat || title) && <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'end' }} className='u-mbs'>
      { goBack && <div>
        <a className="linkBack linkBack--alternate" onClick={ goBack }>{ i18nScoped.t('back') }</a>
      </div> }

      { title }

      <div style={{ flexGrow: 1 }} />

      { !isNoVat && !offeredService.enable_employee_of_customer && <div>
        { [false, true].map(value => <div key={ value ? 'on' : 'off' } className="inputRadio u-mls" style={{ display: 'inline-block' }}>
          <input
            type="radio"
            checked={ quotation.round_on_vat_price === value }
            id={ `quotation_round_on_vat_price_${ value ? 'on' : 'off' }` }
            onChange={ () => onChange({ round_on_vat_price: value }) } />

          <label htmlFor={ `quotation_round_on_vat_price_${ value ? 'on' : 'off' }` }>
            { i18nScoped.t( `round_on_vat_price_${ value ? 'on' : 'off'}`) }
          </label>
        </div>) }
      </div> }
    </div> }

    <div className={ noBox ? undefined : 'box u-mbs' } style={ noBox ? undefined :  { padding: '1em' } }>
      <p className='txtSmall'>{ i18nScoped.t('tooltip') }</p>

      { casting.rentable_unit_of_work === 'casting'
        ? <QuotationFormUnitOfWorkCastingComponent { ...props } />
        : offeredService.enable_employee_of_customer
        ? <QuotationFormEnableEmployeeOfCustomerComponent { ...props } />
        :  <QuotationFormUnitOfWorkHourDayMonthEventComponent { ...props } /> }

      { currentUserMarket['online_sign?'] && window.currentUser.type === 'Operator' && !hideSignable && <div>
        <div className="inputCheckbox u-mtm">
          <input
            id="signable"
            type="checkbox"
            checked={ quotation.signable }
            onChange={ event => onChange({ signable: event.target.checked }) }/>
          <label htmlFor="signable">{ i18nScoped.t('signable') }</label>
        </div>
      </div> }
    </div>
  </>
}
